import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, Observable, throwError } from 'rxjs';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { CreateLoanRepaymentDto, DataImport, Loan, LoanActionPayload, LoanQueryParams, LoanRepaymentDto, LoanRepaymentQueryParams, ProductLoanDashboard, UploadRepaymentsResponse } from '../../interfaces/responses/loan.interface';
import { toHttpParams } from '../../utils/app.util';

const env = environment

@Injectable({
  providedIn: 'root'
})
export class LoanService {
  private http = inject(HttpClient)

  getPaginatedProductLoans(queryParams: LoanQueryParams) {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loans`;

    return this.http.get<PaginationInterface<Loan[]>>(url, {
      params: toHttpParams(queryParams),
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => new Error(`Error fetching a single product loans ${error}`)))
    );
  }

  getLoanById(id: string, productId?: string): Observable<Loan> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    let url = `${env.apiUrl}/loans/${id}`;
    if (productId) {
      url += `?productId=${productId}`;
    }

    return this.http.get<Loan>(url, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  getDashboardData(productId: string) {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loans/dashboard?productId=${productId}`;

    return this.http.get<ProductLoanDashboard>(url, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => new Error(`Error fetching a single product loans ${error}`)))
    );
  }

  loanAction(id: string, payload: LoanActionPayload): Observable<Loan> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loans/${id}/action`;

    return this.http.patch<Loan>(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getRepayments(queryParams: LoanRepaymentQueryParams): Observable<any> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loan-repayment`;

    return this.http.get<any>(url, {
      params: toHttpParams(queryParams),
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  createRepayment(payload: CreateLoanRepaymentDto): Observable<LoanRepaymentDto> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loan-repayment`;

    return this.http.post<LoanRepaymentDto>(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  deleteRepayment(id: string): Observable<void> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loan-repayment/${id}`;

    return this.http.delete<void>(url, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  triggerWebhook(loanId: string, event: string, webhookUrl?: string): Observable<any> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const endpoint = `${env.apiUrl}/loans/trigger-webhook`;
    const body: Record<string, string> = { loanId, event };
    if (webhookUrl?.trim()) body['url'] = webhookUrl.trim();
    return this.http.post<any>(endpoint, body, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  resendDisbursementEmail(loanId: string): Observable<{ message: string }> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loans/${loanId}/resend-email`;
    return this.http.post<{ message: string }>(url, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  uploadRepayments(file: File): Observable<UploadRepaymentsResponse> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/loan-repayment/upload`;
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<UploadRepaymentsResponse>(url, formData, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  getDataImport(id: string): Observable<DataImport> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('tokenn') : null;
    const url = `${env.apiUrl}/data-import/${id}`;

    return this.http.get<DataImport>(url, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

}
